import { $applyNodeReplacement, type ElementDOMSlot, ElementNode, type LexicalNode, type NodeKey, type SerializedElementNode, type Spread } from 'lexical'
import classNames from 'classnames'
import { $variableLabels, badgesElementIn, createBadgesElement } from '../badge'
import styles from '../EmailEditor.module.scss'
import { type SlotAttrs, type SlotFormat } from '../emailTemplateTypes'
import { $getEmailNode, $isEmailNode } from './EmailNode'
import { $isGroupNode } from './GroupNode'
import { optionalBadges, requireBoolean, requireNumber, requireString } from './serializedKeys'
import { $isVariableNode } from './VariableNode'
import { displayStyleForWrapper, isButtonWrapper } from './wrapperStyle'

export type SerializedSlotNode = Spread<SlotAttrs, SerializedElementNode>

const NODE = 'email-slot'

const BOX_CLASSES = 'rounded-3'
const TEXT_PADDING_CLASSES = 'py-2 zocks-px-0.75'

const readSlotFormat = (json: Record<string, unknown>): SlotFormat => {
  const format = requireString(json, NODE, 'slotFormat')
  if (format !== 'plain' && format !== 'rich') {
    throw new Error(`${NODE} needs slotFormat`)
  }
  return format
}

const slotPlaceholder = ({ label, required }: Pick<SlotAttrs, 'label' | 'required'>): string =>
  required ? `Add a ${label.toLowerCase()}` : `Add a ${label.toLowerCase()} (optional)`

export class SlotNode extends ElementNode {
  __attrs: SlotAttrs

  static getType(): string {
    return NODE
  }

  static clone(node: SlotNode): SlotNode {
    return new SlotNode(node.__attrs, node.__key)
  }

  constructor(attrs: SlotAttrs, key?: NodeKey) {
    super(key)
    this.__attrs = attrs
  }

  static importJSON(json: SerializedSlotNode): SlotNode {
    const raw = json as unknown as Record<string, unknown>
    const badges = optionalBadges(raw, NODE)
    const attrs: SlotAttrs = {
      slotId: requireString(raw, NODE, 'slotId'),
      label: requireString(raw, NODE, 'label'),
      slotFormat: readSlotFormat(raw),
      editable: requireBoolean(raw, NODE, 'editable'),
      required: requireBoolean(raw, NODE, 'required'),
      maxLength: requireNumber(raw, NODE, 'maxLength'),
      wrapper: requireString(raw, NODE, 'wrapper'),
      ...(badges === undefined ? {} : { badges }),
    }
    return $createSlotNode(attrs).updateFromJSON(json)
  }

  exportJSON(): SerializedSlotNode {
    const { badges, ...attrs } = this.__attrs
    return { ...super.exportJSON(), ...attrs, ...(badges?.length ? { badges } : {}) }
  }

  createDOM(): HTMLElement {
    const attrs = this.__attrs
    const parent = this.getParent()
    const subject = $isEmailNode(parent) && this.getPreviousSibling() === null
    const lastInBox = $isGroupNode(parent) && parent.getBadges().length > 0 && this.getNextSibling() === null
    const dom = document.createElement('div')
    const button = isButtonWrapper(attrs.wrapper)
    dom.className = classNames(styles.slot, {
      [BOX_CLASSES]: !subject,
      'zocks-mb-0.75': !subject && !lastInBox,
      [TEXT_PADDING_CLASSES]: !subject && !button,
      [styles.slotButton]: button,
      [styles.slotRich]: attrs.slotFormat === 'rich',
      [styles.slotEditable]: attrs.editable,
      [styles.slotLocked]: !attrs.editable,
      [styles.subjectSlot]: subject,
    })
    dom.style.cssText = displayStyleForWrapper(attrs.wrapper)
    dom.dataset.slot = attrs.slotId
    dom.dataset.placeholder = slotPlaceholder(attrs)
    dom.dataset.label = attrs.label
    if (attrs.badges?.length) {
      dom.prepend(createBadgesElement(attrs.badges, $variableLabels()))
    }
    if (!attrs.editable) {
      dom.contentEditable = 'false'
    }
    return dom
  }

  updateDOM(): false {
    return false
  }

  getDOMSlot(element: HTMLElement): ElementDOMSlot<HTMLElement> {
    return super.getDOMSlot(element).withAfter(badgesElementIn(element))
  }

  getAttrs(): SlotAttrs {
    return this.getLatest().__attrs
  }

  isEditable(): boolean {
    return this.getLatest().__attrs.editable
  }

  isShadowRoot(): true {
    return true
  }

  canBeEmpty(): false {
    return false
  }

  canIndent(): false {
    return false
  }
}

export const $createSlotNode = (attrs: SlotAttrs): SlotNode => $applyNodeReplacement(new SlotNode(attrs))

export const $isSlotNode = (node: LexicalNode | null | undefined): node is SlotNode => node instanceof SlotNode

export const $isEmptySlot = (slot: SlotNode): boolean => slot.getTextContent().trim() === '' && !slot.getAllTextNodes().some($isVariableNode)

const $slotsIn = (nodes: LexicalNode[]): SlotNode[] =>
  nodes.flatMap((child) => ($isSlotNode(child) ? [child] : $isGroupNode(child) ? $slotsIn(child.getChildren()) : []))

export const $getSlots = (): SlotNode[] => $slotsIn($getEmailNode()?.getChildren() ?? [])

export const $findSlot = (node: LexicalNode | null): SlotNode | null => {
  if (node === null) {
    return null
  }
  return $isSlotNode(node) ? node : $findSlot(node.getParent())
}
